import React from "react";

import {useConfirmationModalContext} from "../../../components/ConfirmationModalContextProvider";
import {
  DEFAULT_UNSAVED_CHANGES_DIALOG,
  revertStepToBaseline,
  snapshotStep,
} from "./unsavedChangesUtils";

/**
 * Guards navigation away from a step that has unsaved (unpersisted) changes.
 * Shared by Onboarding (RiskProfilingDataProvider) and Trading (TradesDataProvider).
 *
 * On a guarded in-app action (Back / Skip / menu jump) the user is asked to
 * confirm; on confirmation the current step's edits are reverted and the action
 * runs. Route changes and tab close/refresh are also intercepted.
 * "Next / Save & Continue" is intentionally not guarded - it persists.
 *
 * @param {Object} params
 * @param {Object} params.stepRef - Current stepRef.
 * @param {Function} params.setStep - React setter for the current step.
 * @param {Object} [params.history] - react-router history (enables route guard).
 * @param {Object} [params.bypassGuardRef] - disable the guard entirely.
 * @param {Number} [params.baselineVersion] - bumped by the provider once a step is fully initialized
 */
export const useUnsavedChangesGuard = ({stepRef, setStep, history, bypassGuardRef, baselineVersion} = {}) => {
  const modalContext = useConfirmationModalContext();

  const baselineRef = React.useRef(undefined);

  React.useEffect(() => {
    baselineRef.current = snapshotStep(stepRef.current);
    // Re-baseline when the active step changes or step finished initialization via baselineVersion.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepRef.current && stepRef.current.uid, baselineVersion]);

  const isDirty = React.useCallback(
    () => {
      if (bypassGuardRef.current || !stepRef.current || baselineRef.current === undefined) return false;
      return snapshotStep(stepRef.current) !== baselineRef.current;
    },
    []
  );

  const confirmLeave = React.useCallback(
    () => modalContext.confirm({
      ...DEFAULT_UNSAVED_CHANGES_DIALOG,
      variant: 'danger'
    }),
    [modalContext]
  );

  const confirmLeaveRef = React.useRef(confirmLeave);
  confirmLeaveRef.current = confirmLeave;

  const guardedNavigate = React.useCallback(
    async (action) => {
      if (!isDirty()) {
        return action();
      }
      if (await confirmLeave()) {
        if (revertStepToBaseline(stepRef.current, baselineRef.current)) {
          setStep({...stepRef.current});
        }
        return action();
      }
      return undefined;
    },
    [isDirty, confirmLeave, setStep]
  );

  const guardUnsaved = React.useCallback(
    (action) => (...args) => guardedNavigate(() => action(...args)),
    [guardedNavigate]
  );

  React.useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty()) {
        // Close any pending custom dialog so the user isn't left with a stale
        // dialog behind the browser's native prompt if they choose to stay.
        modalContext.dismiss();
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty, modalContext]);

  React.useEffect(() => {
    if (!history || typeof history.block !== "function") {
      return undefined;
    }

    // add history change blocker callback
    const unblock = history.block((targetLocation, action) => {
      if (!isDirty()) {
        return true;
      }

      confirmLeaveRef.current().then((confirmed) => {
        if (!confirmed) return;

        unblock();
        const navigate = action === "REPLACE" ? history.replace : history.push;
        navigate(targetLocation);
      });

      return false;
    });

    return () => unblock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {guardUnsaved};
};
